'use client'

import { createContext, FC, PropsWithChildren, useContext, useReducer } from 'react'
import {
  BookingCartState,
  defaultState,
  GuestSlot,
  RequestDTO,
  VehicleSlot,
} from '@/root/shared-providers/BookingCart/types'
import { bookingCartReducer, BookingCartStateContextProps } from '@/root/shared-providers/BookingCart/reducer'
import type { VehicleDetailsState } from '@/root/shared-types/reservation/reservation-types'

export const BookingCartContext = createContext<BookingCartStateContextProps>({} as BookingCartStateContextProps)

const makeCurrentRequest = ({ passengers, vehicles }: BookingCartState) => {
  return {
    ...(passengers.length && {
      passengerTickets: passengers.map((passenger) => ({
        quantity: passenger.quantity,
        priceCategory: passenger.priceCategory,
        sailPackages: passenger.sailPackages,
        passengers: passenger.slots.map((guestSlot: GuestSlot) => ({
          seqN: guestSlot.seqNum,
          firstName: guestSlot.firstName,
          lastName: guestSlot.lastName,
          gender: guestSlot.gender,
          birthday: guestSlot.birthday,
        })),
      })),
    }),
    ...(vehicles.length && {
      vehicleTickets: vehicles.map((vehicle) => ({
        quantity: vehicle.quantity,
        priceCategory: vehicle.priceCategory,
        sailPackages: vehicle.sailPackages,
        vehicles: vehicle.slots
          .map((slot) => slot as VehicleSlot)
          .map((vehicleSlot): { seqN: number } & VehicleDetailsState => ({
            seqN: vehicleSlot.seqNum,
            licencePlate: vehicleSlot.licencePlate,
            weightInKg: vehicleSlot.weightInKg,
            heightInCm: vehicleSlot.heightInCm,
            widthInCm: vehicleSlot.widthInCm,
            lengthInCm: vehicleSlot.lengthInCm,
          })),
      })),
    }),
  } satisfies RequestDTO
}

const BookingCartProvider: FC<PropsWithChildren> = ({ children }) => {
  const value = { ...defaultState }
  const [state, dispatch] = useReducer(bookingCartReducer, value)

  const exposed = {
    state: { ...state, currentRequest: makeCurrentRequest(state) },
    dispatch,
  }

  return <BookingCartContext.Provider value={exposed}>{children}</BookingCartContext.Provider>
}

export const useBookingCart = () => useContext(BookingCartContext)
export default BookingCartProvider
